const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const toLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseLocalDateKey = (value: string | null): Date | null => {
  if (!value || !LOCAL_DATE_PATTERN.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

// DATE values describe a calendar day; only timestamps may carry a timezone.
export const parseDateValue = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  if (LOCAL_DATE_PATTERN.test(value)) return parseLocalDateKey(value);
  const date = new Date(value.replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T'));
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatLocalDate = (
  value: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string => parseDateValue(value)?.toLocaleDateString('es-UY', options) ?? '-';

export const calendarDaysBetween = (start: Date, end: Date): number => {
  const calendarTime = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((calendarTime(end) - calendarTime(start)) / 86400000);
};

export const addLocalDays = (date: Date, amount: number): Date => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + amount);
  return nextDate;
};

export const formatReceiptDayHeading = (date: Date, today = new Date()): string => {
  const dateKey = toLocalDateKey(date);
  const todayKey = toLocalDateKey(today);
  const yesterdayKey = toLocalDateKey(addLocalDays(today, -1));
  const includeYear = date.getFullYear() !== today.getFullYear();
  const formattedDate = new Intl.DateTimeFormat('es-UY', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(includeYear ? { year: 'numeric' } : {}),
  }).format(date).replace(',', '');

  if (dateKey === todayKey) return `Hoy, ${formattedDate}`;
  if (dateKey === yesterdayKey) return `Ayer, ${formattedDate}`;
  return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
};
